import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { GatingStrategyType } from "../strategies/gating-strategy";
import type { AuthorizationRuleForGating } from "../use-gating-permissions";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { useGatingEvaluator } from "../evaluation/use-gating-evaluator";

export interface UpgradePathDefinition {
  readonly type: GatingStrategyType;
  readonly icon: string;
  readonly titleKey: string;
  readonly descriptionKey: string;
}

/**
 * Registry of upgrade path UI definitions.
 * Mirrors gating-strategy-registry.ts but for display metadata.
 *
 * To add a new gating type:
 * 1. Add a GatingStrategyType value in strategies/gating-strategy.ts
 * 2. Add a strategy in gating-strategy-registry.ts
 * 3. Add a path definition here
 * 4. Add a capability flag check to useCapabilityChecks
 * 5. Add i18n keys under channel.gating.upgradePaths.<type>
 * No component changes needed.
 */
const UPGRADE_PATH_REGISTRY: Record<GatingStrategyType, UpgradePathDefinition> =
  {
    [GatingStrategyType.membership]: {
      type: GatingStrategyType.membership,
      icon: "mdi:card-account-details-star",
      titleKey: "channel.gating.upgradePaths.membership.title",
      descriptionKey: "channel.gating.upgradePaths.membership.description",
    },
    [GatingStrategyType.token]: {
      type: GatingStrategyType.token,
      icon: "mdi:wallet",
      titleKey: "channel.gating.upgradePaths.token.title",
      descriptionKey: "channel.gating.upgradePaths.token.description",
    },
    [GatingStrategyType.points]: {
      type: GatingStrategyType.points,
      icon: "mdi:star-circle",
      titleKey: "channel.gating.upgradePaths.points.title",
      descriptionKey: "channel.gating.upgradePaths.points.description",
    },
    [GatingStrategyType.product]: {
      type: GatingStrategyType.product,
      icon: "mdi:shopping",
      titleKey: "channel.gating.upgradePaths.product.title",
      descriptionKey: "channel.gating.upgradePaths.product.description",
    },
  };

export type CapabilityCheckMap = Record<GatingStrategyType, () => boolean>;

/**
 * Maps a GatingStrategyType to the capability check.
 */
export function useCapabilityCheck() {
  const {
    canUseMembershipGating,
    canUseTokenGating,
    canUsePointsGating,
    canUseProductsGating,
  } = useChannelCapabilities();

  const checks: CapabilityCheckMap = {
    [GatingStrategyType.membership]: () => canUseMembershipGating.value,
    [GatingStrategyType.token]: () => canUseTokenGating.value,
    [GatingStrategyType.points]: () => canUsePointsGating.value,
    [GatingStrategyType.product]: () => canUseProductsGating.value,
  };

  return checks;
}

/**
 * Composable that resolves the available upgrade paths for a given authorization rule.
 *
 * This is the single source of truth for path availability.
 * Both the access welcome modal and the upgrade path chooser consume it.
 *
 * @example
 * const { availablePaths, hasMultiplePaths, singlePath } = useUpgradePaths(rule);
 */
export function useUpgradePaths(
  rule: MaybeRefOrGetter<AuthorizationRuleForGating | null>,
) {
  const capabilityCheck = useCapabilityCheck();
  const { getGatingTypesForRule } = useGatingEvaluator();

  const availablePaths = computed((): UpgradePathDefinition[] => {
    const r = toValue(rule);

    // A null rule means a CHANNEL-WIDE upgrade intent: the channel has no
    // channel-level authorization rule, so there is nothing to intersect
    // against. Every gating type is then evaluable and the capability checks
    // alone determine which paths the channel offers. With a real rule the
    // behavior is unchanged: paths = (rule's gating types) ∩ (capabilities).
    const evaluableTypes = r
      ? getGatingTypesForRule(r)
      : Object.values(GatingStrategyType);

    return Object.values(UPGRADE_PATH_REGISTRY).filter((path) => {
      const canEvaluate = evaluableTypes.includes(path.type);
      const hasCapability = capabilityCheck[path.type]();
      return canEvaluate && hasCapability;
    });
  });

  const hasMultiplePaths = computed(() => availablePaths.value.length > 1);

  const singlePath = computed(() =>
    availablePaths.value.length === 1 ? availablePaths.value[0] : null,
  );

  return {
    availablePaths,
    hasMultiplePaths,
    singlePath,
  };
}

export function getUpgradePaths(
  rule: AuthorizationRuleForGating | null,
  capabilityCheckMap: CapabilityCheckMap,
  ruleGatingTypes: GatingStrategyType[],
) {
  // A null rule means a CHANNEL-WIDE upgrade intent: there is no
  // channel-level authorization rule to intersect against, so every gating type
  // is evaluable and the capability checks alone decide the available paths.
  // `ruleGatingTypes` is ignored in that case. With a real rule the behavior is
  // unchanged: paths = (rule's gating types) ∩ (capabilities).
  const evaluableTypes = rule
    ? ruleGatingTypes
    : Object.values(GatingStrategyType);

  const availablePaths = Object.values(UPGRADE_PATH_REGISTRY).filter((path) => {
    const canEvaluate = evaluableTypes.includes(path.type);
    const hasCapability = capabilityCheckMap[path.type]();

    return canEvaluate && hasCapability;
  });

  const hasMultiplePaths = availablePaths.length > 1;

  const singlePath = availablePaths.length === 1 ? availablePaths[0] : null;

  return {
    availablePaths,
    hasMultiplePaths,
    singlePath,
  };
}
